import React, { useEffect, useState } from 'react';
import { useCookies, withCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import Header from '../components/Header';

const RegistrationForm = ({ api }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [editedUsername, setEditedUsername] = useState(false)

  const [dateValue, setDateValue] = useState("");
  const [dateParts, setDateParts] = useState(null);

  const [privateProfile, setPrivate] = useState(false);
  const [error, setError] = useState('');

  const [loading, setLoading] = useState(false)
  const [showEmailForm, setShowEmailForm] = useState(false);

  const [cookies, setCookie] = useCookies([])

  const navigate = useNavigate()

    useEffect(() => {
            (async () => {
                if (cookies.token) {
                    navigate("/home")
                }
            })()
        }, [])
  

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!username.trim()) {
      setError("Username is required.");
      return
    } else if (!/^[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]$/.test(username)) {
      setError(
        "Username must start and end with a letter or number and can only contain letters, numbers, hyphens, and periods."
      );
      return
    }

    if (!loading) {
      setLoading(true)
      try {

        const resp = await api().signup({
          firstName: firstName,
          lastName: lastName,
          email: email,
          username: username,
          password: password,
          birthMonth: dateParts['month'],
          birthDay: dateParts['day'],
          isPrivate: privateProfile
        })

        if (resp.status === 200) {
          setCookie('token', resp.data.authToken)

          // Email signup includes birthday, but check onboardingComplete flag
          if (resp.data.user && resp.data.user.onboardingComplete === false) {
            navigate('/onboarding');
          } else {
            navigate('/home');
          }
        }

      } catch (error) {
        setError(error?.response?.data?.message ?? "Something went wrong")
      }
      setLoading(false)
    }

  };

  const handleDateChange = (event) => {
    const selectedDate = event.target.value;
    setDateValue(selectedDate);

    if (selectedDate) {
      const date = new Date(selectedDate);
      const day = date.getUTCDate();
      const month = date.getUTCMonth() + 1;
      const year = date.getUTCFullYear();

      const parts = { day, month, year }
      setDateParts(parts);

    }
  };

  const handleUsernameChange = (e) => {
    const input = e.target.value.replace(/[^a-zA-Z0-9.-]/g, "");
    setUsername(input);
    setEditedUsername(true)
  };

  useEffect(() => {
    if (!editedUsername) {
      const generatedUsername = `${firstName.trim().toLowerCase()}${firstName && lastName ? "." : ""}${lastName.trim().toLowerCase()}`;
      setUsername(generatedUsername || "");
    }
  }, [firstName, lastName, editedUsername]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      const res = await api().loginWithGoogle(credentialResponse.credential);

      if (res.status === 200) {
        setCookie('token', res.data.authToken);

        // Check if user needs onboarding
        if (res.data.user.onboardingComplete === false || !res.data.user.birthMonth || !res.data.user.birthDay) {
          navigate('/onboarding');
        } else {
          navigate('/home');
        }
      }
    } catch (error) {
      setError('Google sign-in failed. Please try again.');
    }
    setLoading(false);
  };

  const handleGoogleError = () => {
    setError('Google sign-in failed. Please try again.');
  };

  const handleToggleEmailForm = () => {
    setShowEmailForm(!showEmailForm);
  };

  return (
    <div className="bg-gradient-to-b from-blue-400 to-green-700 pt-16 min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-sm w-full">
        <h2 className="text-2xl font-semibold mb-6 text-center">Sign up</h2>
        {error && <p className="text-red-500 text-center mb-4">{error}</p>}

        <div className="mb-4 w-full flex justify-center px-4">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            theme="outline"
            size="large"
            text="signup_with"
            width="320"
          />
        </div>

        <div className="text-center my-4">
          <button
            type="button"
            onClick={handleToggleEmailForm}
            disabled={loading}
            className="text-sm text-gray-500 hover:text-gray-700 hover:underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {showEmailForm ? 'hide email signup' : 'or sign up with email'}
          </button>
        </div>

        {showEmailForm && (
          <div className="border-t border-gray-200 pt-6">
            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  id="email"
                  className="w-full p-2 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-f-500"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">First name</label>
                <input
                  type="text"
                  id="firstName"
                  className="w-full p-2 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-f-500"
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value.replace(/[^a-zA-Z0-9\s]/g, ""))}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Last name</label>
                <input
                  type="text"
                  id="lastName"
                  className="w-full p-2 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-f-500"
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value.replace(/[^a-zA-Z0-9\s]/g, ""))}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700">Username</label>
                <input
                  type="text"
                  id="username"
                  className="w-full p-2 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-f-500"
                  placeholder="Username"
                  value={username}
                  onChange={handleUsernameChange}
                  onFous={() => setEditedUsername(true)}
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <input
                  type="password"
                  id="password"
                  className="w-full p-2 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <div className="mb-6">
                <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700">Your birthday</label>
                <input
                  type="date"
                  id="birthDate"
                  className="w-full p-2 mt-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={dateValue}
                  onChange={handleDateChange}
                />
              </div>

              <div className="mb-6 flex items-center">
                <input
                  type="checkbox"
                  id="privateProfile"
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={privateProfile}
                  onChange={(e) => setPrivate(e.target.checked)}
                />
                <label htmlFor="privateProfile" className="ml-2 text-sm text-gray-700">
                  Private profile
                </label>
              </div>

              <button
                type="submit"
                className="w-full p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none"
              >
                Sign up
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};


const Register = withCookies(({ api }) => (
  <div>
    <Header api={api} />
    <RegistrationForm api={api} />
  </div>
))

export default Register;